import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Check, Loader2 } from 'lucide-react';
import { fetchPlans, startCheckout, handleCheckoutError } from '../services/checkout';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const PLAN_GROUPS = [
  {
    name: 'Per User',
    description: 'Scale user by user. Cancel anytime.',
    monthly: 'per_user_monthly',
    annual: 'per_user_annual',
    seatsEditable: true,
    badge: null,
  },
  {
    name: 'Lifetime',
    description: 'Never receive a bill from us again.',
    lifetime: 'lifetime',
    seatsEditable: true,
    badge: 'Pay once. Done.',
    highlight: true,
  },
  {
    name: 'Unlimited',
    description: 'One flat rate. Add anyone.',
    monthly: 'unlimited_monthly',
    annual: 'unlimited_annual',
    seatsEditable: false,
    badge: null,
  },
];

function formatCurrency(amount) {
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
  });
}

export default function BillingPage() {
  const { company, refresh } = useAuth();
  const [plans, setPlans] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' | 'annual'
  const [seats, setSeats] = useState(1);
  const [busyPriceId, setBusyPriceId] = useState(null);

  useEffect(() => {
    fetchPlans().then(setPlans).catch(() => toast.error('Could not load plans'));
  }, []);

  const onCheckout = async (priceId) => {
    if (busyPriceId) return;
    setBusyPriceId(priceId);
    try {
      await startCheckout(priceId, seats);
      // startCheckout redirects to Stripe — execution doesn't reach here
    } catch (err) {
      handleCheckoutError(err);
      setBusyPriceId(null);
    }
  };

  const subscription = company?.subscription_status || 'trial';
  const currentPlan = company?.plan || 'trial';
  const isLifetime = subscription === 'lifetime';

  return (
    <div className="space-y-8" data-testid="billing-page">
      {/* Current plan banner */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-sm text-gray-500 mb-1">Current plan</div>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold text-gray-900 capitalize" data-testid="current-plan-name">
              {currentPlan === 'trial' ? 'Trial' : currentPlan.replace(/_/g, ' ')}
            </h2>
            <Badge
              variant="outline"
              className={
                subscription === 'active' || subscription === 'lifetime'
                  ? 'border-green-200 bg-green-50 text-green-700'
                  : 'border-amber-200 bg-amber-50 text-amber-700'
              }
              data-testid="subscription-status-badge"
            >
              {subscription}
            </Badge>
          </div>
          {company?.seats ? (
            <div className="text-sm text-gray-500 mt-1">{company.seats} seat{company.seats > 1 ? 's' : ''}</div>
          ) : null}
        </div>
        <div className="text-sm text-gray-500">
          <button onClick={() => refresh()} className="underline hover:text-gray-900">Refresh status</button>
        </div>
      </div>

      {isLifetime && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 text-amber-900">
          <strong>You're on Lifetime.</strong> You'll never receive another bill from us. Enjoy. ☀️
        </div>
      )}

      {/* Cycle + seat controls */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="inline-flex rounded-full border border-gray-200 p-1 bg-white" role="tablist">
          <button
            data-testid="billing-cycle-monthly"
            onClick={() => setBillingCycle('monthly')}
            className={`px-4 py-1.5 text-sm rounded-full transition ${
              billingCycle === 'monthly' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Monthly
          </button>
          <button
            data-testid="billing-cycle-annual"
            onClick={() => setBillingCycle('annual')}
            className={`px-4 py-1.5 text-sm rounded-full transition ${
              billingCycle === 'annual' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Annual <span className="text-green-600 ml-1">save 15%</span>
          </button>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Seats</label>
          <Input
            data-testid="billing-seats-input"
            type="number"
            min={1}
            max={1000}
            value={seats}
            onChange={(e) => setSeats(Math.max(1, parseInt(e.target.value || '1', 10)))}
            className="w-20 text-center"
          />
          <span className="text-xs text-gray-400 hidden sm:inline">(used for Per User & Lifetime)</span>
        </div>
      </div>

      {/* Plans grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {PLAN_GROUPS.map((group) => {
          const priceId =
            group.lifetime ||
            (billingCycle === 'annual' ? group.annual : group.monthly);
          const plan = plans?.[priceId];
          const unitAmount = plan?.amount ?? 0;
          const isUnlimited = group.name === 'Unlimited';
          const isLifetimeCard = !!group.lifetime;
          const effectiveSeats = isUnlimited ? 1 : seats;
          const total = unitAmount * effectiveSeats;
          const busy = busyPriceId === priceId;

          return (
            <div
              key={group.name}
              data-testid={`billing-card-${group.name.toLowerCase().replace(/\s+/g, '-')}`}
              className={`border-2 rounded-lg p-6 ${
                group.highlight ? 'border-gray-900 shadow-lg' : 'border-gray-200'
              }`}
            >
              {group.badge && (
                <Badge className="mb-3 bg-gray-900 text-white border-0">{group.badge}</Badge>
              )}
              <div className="text-sm text-gray-600 mb-1">{group.name}</div>
              <div className="text-xs text-gray-500 mb-4">{group.description}</div>

              <div className="mb-4">
                <div className="text-4xl font-bold text-gray-900">
                  {formatCurrency(unitAmount)}
                </div>
                <div className="text-xs text-gray-500">
                  {isLifetimeCard
                    ? 'one-time · per user'
                    : isUnlimited
                    ? billingCycle === 'annual'
                      ? 'flat rate · billed annually'
                      : 'flat rate · per month'
                    : billingCycle === 'annual'
                    ? 'per user · billed annually'
                    : 'per user · per month'}
                </div>
              </div>

              {effectiveSeats > 1 && !isUnlimited && (
                <div className="bg-gray-50 rounded-md px-3 py-2 mb-4 text-xs text-gray-700" data-testid={`billing-total-${group.name.toLowerCase().replace(/\s+/g, '-')}`}>
                  {effectiveSeats} × {formatCurrency(unitAmount)} = <strong>{formatCurrency(total)}</strong>
                  {isLifetimeCard ? ' (one-time)' : ''}
                </div>
              )}

              <Button
                data-testid={`checkout-btn-${group.name.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => onCheckout(priceId)}
                disabled={busy || !plan || (currentPlan === group.name.toLowerCase().replace(' ', '_') && !isLifetime)}
                className={`w-full ${
                  group.highlight
                    ? 'bg-gray-900 hover:bg-gray-800 text-white'
                    : 'bg-white text-gray-900 border-2 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {busy ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : isLifetimeCard ? (
                  'Get Lifetime'
                ) : (
                  'Upgrade'
                )}
              </Button>

              <ul className="space-y-2 mt-5 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-gray-900 mt-0.5 flex-shrink-0" />
                  <span>Price locked for life</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-gray-900 mt-0.5 flex-shrink-0" />
                  <span>{isLifetimeCard ? 'No subscriptions, no renewals' : 'Cancel anytime · no contracts'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-gray-900 mt-0.5 flex-shrink-0" />
                  <span>{isUnlimited ? 'Unlimited users' : isLifetimeCard ? 'Transfer between team members' : 'AI-powered ticket handling'}</span>
                </li>
              </ul>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-gray-400 text-center">
        Payments processed securely by Stripe. Need enterprise SSO, custom AI training, or 99.9% SLA?{' '}
        <a href="mailto:hello@simplifiedhelpdesk.com" className="text-gray-600 underline">
          Contact sales
        </a>.
      </p>
    </div>
  );
}
